import type { GoalRiskInheritance } from "@repo/shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { goalSnapshotQueryKeys } from "./use-goal-snapshots";
import { useInvalidatePortfolioSnapshots } from "./use-portfolio-snapshots";
import { useInvalidateDynamicSync } from "./use-portfolios";

/**
 * QL-212 (D13) — apply the refresh hints a goal-create response carries.
 *
 * Under a client-level risk model the new goal is born scored and its model
 * portfolios are already resolved, so the same queries a risk submit moves have
 * moved here too — including the instance-wide cost and expected-value
 * aggregates, which fold the new goal's portfolios in. Every other configuration
 * reports `pending`, which wrote nothing and hints nothing.
 *
 * Advisor surface only. The public flow keeps its own block in
 * `use-public-flow/use-goals.ts`: its keys live under `["publicFlow", …]`, and
 * it skips the goal-snapshot invalidation because `["publicFlow","goals"]` is a
 * prefix of every per-goal snapshot key. The advisor goals key
 * (`["flowInstances", instanceId, "goals"]`) is not, hence the explicit call.
 */
export function useApplyGoalRiskInheritance(instanceId: string) {
  const queryClient = useQueryClient();
  const invalidatePortfolioSnapshots = useInvalidatePortfolioSnapshots();
  const invalidateDynamicSync = useInvalidateDynamicSync();

  return (goalId: string, inheritance?: GoalRiskInheritance) => {
    if (inheritance?.outcome.status !== "applied") return;
    queryClient.invalidateQueries({ queryKey: goalSnapshotQueryKeys.instanceCost(instanceId) });
    queryClient.invalidateQueries({
      queryKey: goalSnapshotQueryKeys.instanceExpectedValue(instanceId),
    });
    queryClient.invalidateQueries({ queryKey: goalSnapshotQueryKeys.allForGoal(goalId) });
    for (const hint of inheritance.refreshHints) {
      invalidatePortfolioSnapshots(hint.portfolioId, hint);
    }
    invalidateDynamicSync(inheritance.dynamicSync);
  };
}
